import { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  config,
  normalizeSafeUrl,
  openUrlInNewTabSafely,
  sanitizeAnnouncementHtml,
} from '@workspace/public-utils'

interface Announcement {
  id: string;
  title: string;
  content: string;
  announcement_type: string;
  priority: string;
  is_dismissible: boolean;
  image_url?: string;
  link_url?: string;
  link_text?: string;
  start_date: string;
  end_date?: string;
}

const AnnouncementModal = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Check if announcement was dismissed today
  const isAnnouncementDismissed = (announcementId: string): boolean => {
    const dismissalData = localStorage.getItem(`announcement_dismissed_${announcementId}`);
    if (!dismissalData) return false;
    
    try {
      const { expiryTime } = JSON.parse(dismissalData);
      if (Date.now() < expiryTime) {
        return true; // Still within 24 hours
      } else {
        // Expired, remove from storage
        localStorage.removeItem(`announcement_dismissed_${announcementId}`);
        return false;
      }
    } catch {
      // Invalid data, remove it
      localStorage.removeItem(`announcement_dismissed_${announcementId}`);
      return false;
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(
        `${config.API_BASE_URL}/api/v1/announcements/public/active?user_role=customer`
      );

      if (response.ok) {
        const data = await response.json();
        if (data && data.length > 0) {
          // Filter out dismissed announcements
          const activeAnnouncements = data.filter(
            (announcement: Announcement) => !isAnnouncementDismissed(announcement.id)
          );
          
          if (activeAnnouncements.length > 0) {
            setAnnouncements(activeAnnouncements);
            setIsOpen(true);
            
            // Track view for the first announcement
            if (activeAnnouncements[0]?.id) {
              trackView(activeAnnouncements[0].id);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const trackView = async (announcementId: string) => {
    try {
      await fetch(
        `${config.API_BASE_URL}/api/v1/announcements/${announcementId}/view`,
        { method: 'POST' }
      );
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const trackClick = async (announcementId: string) => {
    try {
      await fetch(
        `${config.API_BASE_URL}/api/v1/announcements/${announcementId}/click`,
        { method: 'POST' }
      );
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDismissToday = () => {
    // Store dismissal with expiry (24 hours from now)
    const expiryTime = Date.now() + (24 * 60 * 60 * 1000); // 24 hours in milliseconds
    announcements.forEach(announcement => {
      localStorage.setItem(
        `announcement_dismissed_${announcement.id}`,
        JSON.stringify({ expiryTime })
      );
    });
    setIsOpen(false);
  };

  const handleNext = () => {
    if (currentIndex < announcements.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      
      // Track view for next announcement
      if (announcements[newIndex]?.id) {
        trackView(announcements[newIndex].id);
      }
    } else {
      handleClose();
    }
  };

  const currentAnnouncement = announcements[currentIndex];
  const sanitizedContent = useMemo(
    () => sanitizeAnnouncementHtml(currentAnnouncement?.content ?? ''),
    [currentAnnouncement?.content]
  );
  const safeLinkUrl = useMemo(
    () => normalizeSafeUrl(currentAnnouncement?.link_url),
    [currentAnnouncement?.link_url]
  );

  const handleLinkClick = () => {
    if (!safeLinkUrl) return;
    if (!currentAnnouncement) return;
    trackClick(currentAnnouncement.id);
    openUrlInNewTabSafely(safeLinkUrl);
  };

  if (!isOpen || announcements.length === 0 || !currentAnnouncement) {
    return null;
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500';
      case 'high':
        return 'bg-orange-500';
      case 'normal':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return '긴급';
      case 'high':
        return '중요';
      case 'normal':
        return '일반';
      case 'low':
        return '참고';
      default:
        return '';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${getPriorityColor(currentAnnouncement.priority)}`}>
                    {getPriorityLabel(currentAnnouncement.priority)}
                  </span>
                  {announcements.length > 1 && (
                    <span className="text-sm text-gray-500">
                      {currentIndex + 1} / {announcements.length}
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {currentAnnouncement.title}
                </h2>
              </div>
              
              {currentAnnouncement.is_dismissible && (
                <button
                  onClick={handleClose}
                  className="flex-shrink-0 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              )}
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Image */}
              {currentAnnouncement.image_url && (
                <div className="mb-4">
                  <img
                    src={currentAnnouncement.image_url}
                    alt={currentAnnouncement.title}
                    className="w-full rounded-lg object-cover max-h-96"
                  />
                </div>
              )}

              {/* Content */}
              <div 
                className="prose prose-lg max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              />

              {/* Link Button */}
              {safeLinkUrl && currentAnnouncement.link_text && (
                <div className="mt-6">
                  <button
                    onClick={handleLinkClick}
                    className="btn-primary w-full"
                  >
                    {currentAnnouncement.link_text}
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {new Date(currentAnnouncement.start_date).toLocaleDateString('ko-KR')}
              </div>
              
              <div className="flex gap-2">
                {currentAnnouncement.is_dismissible && (
                  <>
                    <button
                      onClick={handleDismissToday}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      오늘 하루 안 보기
                    </button>
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      닫기
                    </button>
                  </>
                )}
                
                {currentIndex < announcements.length - 1 ? (
                  <button
                    onClick={handleNext}
                    className="btn-primary"
                  >
                    다음 공지
                  </button>
                ) : currentAnnouncement.is_dismissible && (
                  <button
                    onClick={handleClose}
                    className="btn-primary"
                  >
                    확인
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AnnouncementModal;

